
export enum ContactTypes {
  Lead = 'lead',
  Customer = 'customer',
  Employee = 'employee',
  Vendor = 'vendor',
  Builder = 'builder',
  Labour = 'labour',
  LabourGroup = 'labour-group',
  BuilderGroup ='builder-group',
  System = 'system'
}

export interface IContactTypes {
  contact_type_code:string;
  contact_type_id:number;
  contact_type_name:string;
  created_at:Date;
  created_by:string;
  updated_at:Date;
  updated_by:string;
}

export const CONTACT_TYPES = Object.entries(ContactTypes).map(([key, value]) => ({ label:key, value:value }));

export interface IContactConversionLog {
  conversion_id: number;
  contact_id: number;
  from_type_id: number;
  to_type_id: number;
  converted_by: number;
  converted_at: string;
  trigger_type: string;
  reference_id?: number;
  notes?: string;
  converter?: {
    id: number;
    firstname?: string;
    lastname?: string;
    email?: string;
  };
  from_type?: IContactTypes;
  to_type?: IContactTypes;
}

export interface IContact {
  account_status: any;
  payments: any[];
  quick_update: boolean;
  contact_id: number;
  image: string;
  image_id: string;
  name: string;
  email: string;
  phone: number;
  website: string;
  facebook: string;
  contact_type: IContactTypes | undefined;
  contact_type_id:number;
  contact_type_code:string;
  notes: number;
  created_by: number;
  created_at: string;
  updated_by: number;
  updated_date: string;
  deleted_at: number;
  billing_address_id: number;
  billing_address:IAddress;
  shipping_address_id: number[];
  shipping_address:IAddress[];
  gst: string;
  status: number;
  due_listing: any;
  accounts:IAccounts | undefined;
  address: IAddress[];
  groups:IContact[];
  contact_group_ids:number[];
  orders:any[];
  expenses:any[];
  worksheets:any[];
  converted_at?: string;
  original_type_id?: number;
  latest_conversion_log?: IContactConversionLog | null;
  conversion_logs?: IContactConversionLog[];
}

export interface IAccounts {
  total_payment: number;
  last_payment: any;
  total_invoiced: number;
  due: number;
}

export class Contact implements IContact {
  account_status: any;
  worksheets!: any[];
  payments!: any[];
  orders!: any[];
  quick_update!: boolean;
  contact_id!: number;
  image!: string;
  image_id!:string;
  name!: string;
  email!: string;
  phone!: number;
  website!: string;
  facebook!: string;
  contact_type!: IContactTypes;
  contact_type_id!:number;
  contact_type_code!:string;
  notes!: number;
  created_by!: number;
  created_at!: string;
  updated_by!: number;
  updated_date!: string;
  deleted_at!: number;
  billing_address_id!: number;
  billing_address!: IAddress;
  shipping_address_id!: number[];
  shipping_address!: IAddress[];
  gst!: string;
  status!: number;
  due_listing!: any;
  accounts!: IAccounts;
  address:IAddress[] = [];
  groups!: IContact[];
  contact_group_ids!: number[];
  expenses!: any[];
  converted_at?: string;
  original_type_id?: number;
  latest_conversion_log?: IContactConversionLog | null;
  conversion_logs?: IContactConversionLog[];
}

export class IAddress {
  address_id!: number;
  address_street!: string;
  address_street_2!: string;
  address_city!: string;
  address_state!: string;
  address_pin_code!: number;
  created_by!: number;
  created_date!: Date;
  updated_by!: number;
  updated_date !: Date;
  address_type!:string;
  address_delete!:boolean;
}

export class ContactSearchTool {
  contact_id?: number;
  contact_type_code?: string;
  contact_name?: string;
  from_date?: string;
  to_date?: string;
  status?: number;
  account_status = '0';
  order_by = 'created_at'
  order_type = 'desc';
}

export type ClosureRequestStatus = 'waiting_for_approval' | 'closed' | 'rejected' | 'cancelled';

export interface IClosureRequester {
  id: number;
  name?: string;
  firstname?: string;
  lastname?: string;
  email?: string;
}

export interface IClosureRequest {
  request_id: number;
  contact_id?: number;
  status: ClosureRequestStatus;
  comments?: string;
  created_at: string;
  updated_at?: string;
  requester?: IClosureRequester;
  approver?: IClosureRequester;
  contact?: IContact;
  outstanding_balance?: number;
}

export interface IClosurePermissions {
  can_request: boolean;
  can_approve: boolean;
  can_reject: boolean;
  can_cancel: boolean;
}

export interface IContactClosureStatus {
  contact_id: number;
  is_account_closed: boolean;
  has_pending_request: boolean;
  latest_request?: IClosureRequest | null;
  permissions: IClosurePermissions;
}

export interface IContactClosureHistoryItem {
  id?: number;
  request_id?: number;
  contact_id?: number;
  status: ClosureRequestStatus;
  comments?: string;
  created_at: string;
  created_by?: number;
  action_by?: IClosureRequester;
  requester?: IClosureRequester;
  approver?: IClosureRequester;
}

export interface IContactClosureHistoryResponse {
  contact_id: number;
  contact_name?: string;
  account_status?: number;
  latest_closure_status?: string;
  history: IContactClosureHistoryItem[];
}

export type IContactClosureHistory = IContactClosureHistoryItem[] | IContactClosureHistoryResponse;

// =========================================================================
// Contact Call Logs
// =========================================================================

export type CallType = 'incoming' | 'outgoing' | 'missed' | 'rejected';

export interface ICallLogUser {
  id: number;
  firstname: string;
  lastname?: string;
  email?: string;
}

export interface ICallLogDevice {
  device_id: number;
  model: string;
  platform: string;
}

export interface IContactCallLog {
  call_log_id: number;
  device_id: number;
  user_id: number;
  contact_id: number;
  phone_number: string;
  normalized_phone?: string;
  call_type: CallType;
  started_at: string;
  duration_seconds: number;
  contact_match_status?: string;
  user?: ICallLogUser;
  device?: ICallLogDevice;
}

export interface IContactCallLogsPagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  data: IContactCallLog[];
}

export interface IContactCallLogsFilter {
  page?: number;
  page_size?: number;
  call_type?: CallType | '';
  from_date?: string;
  to_date?: string;
}

// =========================================================================
// Contact Activities
// =========================================================================

export type ContactActivityType = 'meeting' | 'visit' | 'note' | 'call_summary';

export interface IContactActivityUser {
  id: number;
  firstname: string;
  lastname?: string;
  email?: string;
}

export interface IContactActivityDevice {
  device_id: number;
  model: string;
  platform: string;
}

export interface IContactActivity {
  activity_id: number;
  contact_id: number;
  user_id: number;
  device_id?: number | null;
  activity_type: ContactActivityType;
  activity_at: string;
  title?: string | null;
  comment?: string | null;
  source: 'web' | 'mobile' | string;
  created_at: string;
  user?: IContactActivityUser;
  device?: IContactActivityDevice | null;
}

export interface IContactActivitiesPagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  data: IContactActivity[];
}

export interface IContactActivitiesFilter {
  page?: number;
  page_size?: number;
}

export interface ICreateContactActivityPayload {
  activity_type: ContactActivityType;
  title?: string;
  comment?: string;
  activity_at?: string;
}

// =========================================================================
// Contact Reminders / Follow-Ups
// =========================================================================

export type ReminderStatus = 'pending' | 'completed' | 'cancelled';
export type ReminderActionType = 'complete' | 'reschedule' | 'cancel' | 'comment';

export interface IReminderUser {
  id: number;
  firstname: string;
  lastname?: string;
  email?: string;
}

export interface IReminderAction {
  action_id: number;
  reminder_id: number;
  action_type: ReminderActionType;
  outcome?: string | null;
  comment: string;
  next_followup_at?: string | null;
  occurred_at: string;
  performed_by_user?: IReminderUser;
  device?: IContactActivityDevice | null;
}

export interface IReminderSummary {
  total_pending: number;
  overdue: number;
  today: number;
  upcoming_7_days: number;
  completed: number;
}

export type ReminderPreset = 'today' | 'overdue' | 'upcoming' | 'completed' | '';

export interface IContactReminder {
  reminder_id: number;
  contact_id?: number | null;
  title: string;
  description?: string | null;
  assigned_to?: number;
  created_by?: number;
  due_at: string;
  reminder_status: ReminderStatus;
  completed_at?: string | null;
  completed_by?: number | null;
  created_at: string;
  updated_at?: string;
  contact?: {
    contact_id: number;
    name: string;
    phone?: string;
    contact_type_id?: number;
    is_account_closed?: boolean;
    contact_type?: {
      contact_type_id: number;
      contact_type_name: string;
      contact_type_code: string;
    };
  } | null;
  assigned_user?: IReminderUser;
  created_by_user?: IReminderUser;
  completed_by_user?: IReminderUser;
  actions?: IReminderAction[];
}

export interface IContactRemindersPagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page?: number;
  data: IContactReminder[];
}

export interface IContactRemindersFilter {
  contact_id?: number;
  preset?: ReminderPreset;
  reminder_status?: ReminderStatus | '';
  assigned_to?: number;
  from_date?: string;
  to_date?: string;
  page?: number;
  page_size?: number;
}

export interface ICreateReminderPayload {
  contact_id?: number | null;
  title: string;
  due_at: string;
  description?: string;
  assigned_to?: number;
}

export interface IReminderActionPayload {
  action_type: ReminderActionType;
  comment: string;
  outcome?: string;
  next_followup_at?: string;
}



